import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button } from '../components/Layout';
import api from '../services/api';

// Yes/No selector — module-scope (defining it inside the page re-created it on
// every render, remounting the buttons and dropping focus). Uses the app's real
// accent (bg-yellow-400); the previous bg-keder-yellow isn't defined in this app,
// so the selected label rendered black-on-nothing and looked like it vanished.
function YesNo({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) {
  const base = 'px-6 py-2 rounded-lg font-semibold border transition-colors';
  const on = 'bg-yellow-400 text-gray-900 border-yellow-400';
  const off = 'bg-gray-800 border-gray-600 text-gray-100 hover:bg-gray-700';
  return (
    <div className="flex gap-3">
      <button type="button" onClick={() => onChange(true)} className={`${base} ${value === true ? on : off}`}>Yes</button>
      <button type="button" onClick={() => onChange(false)} className={`${base} ${value === false ? on : off}`}>No</button>
    </div>
  );
}

// Employee-side support request. Impact only — no category, type, or priority.
// workstation/order are auto-captured from the URL when launched from a work card.
export function ReportProblemPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const workstationId = params.get('ws') || undefined;
  const workOrderId = params.get('wo') || undefined;
  const orderId = params.get('order') || undefined;
  const orderNumber = params.get('orderNumber') || undefined;

  const [description, setDescription] = useState('');
  const [canKeepWorking, setCanKeepWorking] = useState<boolean | null>(null);
  const [currentOrderStopped, setCurrentOrderStopped] = useState<boolean | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  const submit = async () => {
    setError('');
    if (!description.trim()) { setError('Please describe what is wrong.'); return; }
    if (canKeepWorking === null) { setError('Please answer: can you keep working?'); return; }
    if (currentOrderStopped === null) { setError('Please answer: is the current order or operation stopped?'); return; }
    setSubmitting(true);
    try {
      const r = await api.createSupportRequest(
        { description: description.trim(), canKeepWorking, currentOrderStopped, workstationId, workOrderId, orderId },
        photo,
      );
      setResult(r);
    } catch (e: any) { setError(e.message || 'Could not submit.'); }
    finally { setSubmitting(false); }
  };

  // Confirmation screen
  if (result) {
    const urgent = result.mustGetSupervisor;
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <Card>
          <div className={`rounded-lg p-5 ${urgent ? 'bg-red-900/40 border-2 border-red-500' : 'bg-green-900/30 border border-green-600'}`}>
            <div className={`text-xl font-bold mb-2 ${urgent ? 'text-red-200' : 'text-green-200'}`}>
              {urgent ? '⚠️ Go get a supervisor now' : '✓ Recorded'}
            </div>
            <p className={`text-lg ${urgent ? 'text-red-100' : 'text-green-100'}`}>{result.confirmation}</p>
            {urgent && <p className="mt-3 text-red-200 font-semibold">Don't wait for a reply. Walk over and tell someone in person.</p>}
          </div>
          <div className="mt-4 text-sm text-gray-400">Reference: {result.requestNumber}</div>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => navigate('/my-requests')}>View my requests</Button>
            <button onClick={() => navigate('/')} className="px-4 py-2 rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-800">Back to dashboard</button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Fixed safety line — always visible, top of the form */}
      <div className="rounded-lg bg-red-900/40 border-2 border-red-500 px-5 py-4">
        <p className="text-red-100 font-bold text-lg">If anyone could get hurt, stop and get a supervisor now. Don't fill this out first.</p>
      </div>

      <Card>
        <h1 className="text-2xl font-bold text-white mb-1">Report a problem</h1>
        {orderNumber
          ? <p className="text-sm text-gray-400 mb-5">Attached to the order you have open ({orderNumber}). We capture your station and order automatically.</p>
          : <p className="text-sm text-gray-400 mb-5">We capture who you are and the time automatically.</p>}

        {error && <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 rounded-lg text-sm">{error}</div>}

        <label className="block text-sm font-medium text-gray-200 mb-1">What's wrong?</label>
        <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white mb-5"
          placeholder="Describe the problem in your own words." />

        <label className="block text-sm font-medium text-gray-200 mb-2">Can you keep working?</label>
        <div className="mb-5"><YesNo value={canKeepWorking} onChange={setCanKeepWorking} /></div>

        <label className="block text-sm font-medium text-gray-200 mb-2">Is the current order or operation stopped?</label>
        <div className="mb-5"><YesNo value={currentOrderStopped} onChange={setCurrentOrderStopped} /></div>

        <label className="block text-sm font-medium text-gray-200 mb-1">Photo <span className="text-gray-500 font-normal">(optional)</span></label>
        <input type="file" accept="image/png,image/jpeg,application/pdf" onChange={e => setPhoto(e.target.files?.[0] || null)}
          className="block text-sm text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-600 file:bg-gray-800 file:text-gray-200 mb-6" />

        <div className="flex gap-3">
          <Button onClick={submit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</Button>
          <Link to="/" className="px-4 py-2 rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-800">Cancel</Link>
        </div>
      </Card>
    </div>
  );
}

export default ReportProblemPage;
